import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, X, Navigation, Heart, ShoppingCart, FileText, ShieldCheck, Clock, Package, Store, ArrowRight, Tag } from 'lucide-react';
import Navbar from '../components/Navbar';
import { LocationPickerModal, UserLocationBar, calcDistance } from '../components/LocationMap';
import { useLocation2 } from '../context/LocationContext';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import API from '../utils/api';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import QuoteRequestModal from '../components/QuoteRequestModal';

const DEFAULT_CATEGORIES = [
  { key: '', label: 'All' },
  { key: 'sand', label: 'Sand & Aggregates' },
  { key: 'cement', label: 'Cement' },
  { key: 'iron', label: 'Steel' },
  { key: 'bricks', label: 'Bricks' },
  { key: 'long_bricks', label: 'Long Bricks' },
  { key: 'short_bricks', label: 'Short Bricks' },
  { key: 'other', label: 'Hardware' }
];

const MATERIAL_CATEGORY_LABELS = {
  sand: 'Sand & Aggregates',
  cement: 'Cement',
  iron: 'Steel',
  bricks: 'Bricks',
  long_bricks: 'Long Bricks',
  short_bricks: 'Short Bricks',
  other: 'Hardware'
};

export default function ShopList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [view, setView] = useState(searchParams.get('view') === 'materials' ? 'materials' : 'shops');
  const [sortBy, setSortBy] = useState('nearest'); // nearest | rating | name
  const [materialSort, setMaterialSort] = useState('lowest');
  const [brandFilter, setBrandFilter] = useState('');
  const [allMaterialBrands, setAllMaterialBrands] = useState([]);
  const [maxPriceFilter, setMaxPriceFilter] = useState(0);
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORIES);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const { location: userLocation, saveLocation } = useLocation2();

  useEffect(() => {
    const nextSearch = searchParams.get('search') || '';
    const nextCategory = searchParams.get('category') || '';
    const nextView = searchParams.get('view') === 'materials' ? 'materials' : 'shops';
    setSearch(nextSearch);
    setCategory(nextCategory);
    setView(nextView);
    fetchData(nextSearch, nextCategory);
  }, [searchParams]);

  useEffect(() => {
    API.get('/admin/public-settings')
      .then(res => {
        const configured = (res.data.homeCategories || [])
          .filter(cat => cat.active !== false && cat.key)
          .map(cat => ({
            key: cat.key,
            label: cat.label || MATERIAL_CATEGORY_LABELS[cat.key] || cat.key.replace(/_/g, ' ')
          }));
        if (configured.length) setCategoryOptions([{ key: '', label: 'All' }, ...configured]);
      })
      .catch(() => {});
  }, []);

  const fetchData = async (nextSearch = search, nextCategory = category) => {
    setLoading(true);
    setError('');
    try {
      const [ownersRes, matsRes] = await Promise.all([
        API.get(`/owners?search=${encodeURIComponent(nextSearch)}`),
        API.get(`/materials?search=${encodeURIComponent(nextSearch)}&category=${encodeURIComponent(nextCategory)}`)
      ]);
      setShops(ownersRes.data);
      setMaterials(matsRes.data);
      if (nextCategory) {
        const allMatsRes = await API.get(`/materials?search=${encodeURIComponent(nextSearch)}`);
        setAllMaterialBrands([...new Set((allMatsRes.data || []).map(m => m.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b)));
      } else {
        setAllMaterialBrands([...new Set((matsRes.data || []).map(m => m.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b)));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load shops and materials');
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ search, category, ...(view === 'materials' ? { view: 'materials' } : {}) });
  };

  const changeCategory = (nextCategory) => {
    setCategory(nextCategory);
    const params = {};
    if (search) params.search = search;
    if (nextCategory) params.category = nextCategory;
    if (view === 'materials') params.view = 'materials';
    setSearchParams(params);
  };

  const switchView = (nextView) => {
    setView(nextView);
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    if (nextView === 'materials') params.view = 'materials';
    setSearchParams(params);
  };

  // Add distance to each shop
  const shopsWithDist = shops
    .filter(s => !search || s.shopName?.toLowerCase().includes(search.toLowerCase()) || s.location?.city?.toLowerCase().includes(search.toLowerCase()))
    .map(s => ({
      ...s,
      distance: userLocation && s.location?.lat && s.location?.lng
        ? calcDistance(userLocation.lat, userLocation.lng, s.location.lat, s.location.lng)
        : null
    }));

  // Sort shops
  const sortedShops = [...shopsWithDist].sort((a, b) => {
    if (sortBy === 'nearest') {
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    }
    if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
    if (sortBy === 'name') return (a.shopName || '').localeCompare(b.shopName || '');
    return 0;
  });

  const materialBrands = allMaterialBrands.length
    ? allMaterialBrands
    : [...new Set(materials.map(m => m.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const maxMaterialPrice = Math.max(0, ...materials.map(m => Number(m.price || 0)));
  const activeMaxPrice = maxPriceFilter || maxMaterialPrice;

  const materialsWithDist = materials.map(mat => ({
    ...mat,
    distance: userLocation && mat.owner?.location?.lat && mat.owner?.location?.lng
      ? calcDistance(userLocation.lat, userLocation.lng, mat.owner.location.lat, mat.owner.location.lng)
      : null
  }));

  const filteredMaterials = materialsWithDist
    .filter(mat => !brandFilter || mat.brand === brandFilter)
    .filter(mat => !activeMaxPrice || Number(mat.price || 0) <= activeMaxPrice)
    .sort((a, b) => {
      if (materialSort === 'lowest') return Number(a.price || 0) - Number(b.price || 0);
      if (materialSort === 'highest') return Number(b.price || 0) - Number(a.price || 0);
      if (materialSort === 'name') return (a.name || '').localeCompare(b.name || '');
      if (materialSort === 'nearest') {
        if (a.distance === null && b.distance === null) return 0;
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }} className="page-with-bottom-nav">
      <Navbar />

      {/* Search Bar */}
      <div className="shop-list-top">
        <div className="shop-list-top-inner">
          <div className="shop-list-heading">
            <div><ShieldCheck size={14} /> Approved supplier network</div>
            <h1>Find trusted material suppliers</h1>
            <p>Compare verified local shops, ratings, distance and active offers.</p>
          </div>

          <form onSubmit={handleSearch} className="shop-list-search">
            <div className="shop-list-search-field">
              <Search size={18} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search shops, materials, brands, location..."
                aria-label="Search shops, materials, brands or locations" />
              {search && <button type="button" onClick={() => setSearch('')} title="Clear search"><X size={15} /></button>}
            </div>
            <button type="submit" className="shop-list-search-submit"><Search size={16} /> Search</button>
          </form>

          {/* Location Bar */}
          <div className="shop-list-location">
            <UserLocationBar location={userLocation} onChangeLocation={() => setShowLocationPicker(true)} />
          </div>

          {view === 'shops' && (
            <div className="shop-category-scroll shop-list-categories">
              {categoryOptions.map(cat => (
                <button key={cat.key} onClick={() => changeCategory(cat.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 20, border: '1.5px solid', borderColor: category === cat.key ? 'var(--orange)' : 'var(--gray-border)', background: category === cat.key ? 'var(--orange-soft)' : 'white', color: category === cat.key ? 'var(--orange)' : 'var(--dark)', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="shop-list-content">
        {/* Controls */}
        <div className="shop-list-controls">
          <div className="shop-list-count">
            <strong>{view === 'shops' ? sortedShops.length : filteredMaterials.length}</strong> {view === 'shops' ? 'shops found' : 'materials found'}
            {userLocation && <span style={{ color: 'var(--blue)', marginLeft: 6 }}> / sorted by distance</span>}
          </div>
          <div className="shop-list-control-actions">
            {/* Sort (only for shops) */}
            {view === 'shops' && (
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                style={{ border: '1.5px solid var(--gray-border)', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, background: 'white', cursor: 'pointer' }}>
                <option value="nearest">Nearest First</option>
                <option value="rating">Top Rated</option>
                <option value="name">Name A-Z</option>
              </select>
            )}
            {view === 'materials' && (
              <select value={materialSort} onChange={e => setMaterialSort(e.target.value)}
                style={{ border: '1.5px solid var(--gray-border)', borderRadius: 12, padding: '10px 16px', fontSize: 14, fontWeight: 600, background: 'white', cursor: 'pointer', minWidth: 170 }}>
                <option value="lowest">Lowest Price</option>
                <option value="highest">Highest Price</option>
                <option value="nearest">Nearest Shop</option>
                <option value="name">Name A-Z</option>
                <option value="newest">Newest</option>
              </select>
            )}
            {/* View Toggle */}
            <div className="shop-view-toggle">
              {[['shops', 'Shops'], ['materials', 'Materials']].map(([v, l]) => (
                <button key={v} onClick={() => switchView(v)}
                  style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: view === v ? 'var(--orange)' : 'transparent', color: view === v ? 'white' : 'var(--gray)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingState label="Finding shops near you..." />
        ) : error ? (
          <ErrorState message={error} onRetry={fetchData} />
        ) : view === 'shops' ? (
          sortedShops.length === 0 ? (
            <EmptyState title="No shops found" message="Try different search terms or clear the category filter." />
          ) : (
            <div className="shops-grid">
              {sortedShops.map(shop => <ShopCard key={shop._id} shop={shop} userLocation={userLocation} />)}
            </div>
          )
        ) : (
          <div className="materials-market-layout">
            <MaterialFilters
              category={category}
              setCategory={changeCategory}
              brandFilter={brandFilter}
              setBrandFilter={setBrandFilter}
              materialBrands={materialBrands}
              activeMaxPrice={activeMaxPrice}
              maxMaterialPrice={maxMaterialPrice}
              setMaxPriceFilter={setMaxPriceFilter}
              categoryOptions={categoryOptions}
            />
            <div className="materials-market-grid">
              {filteredMaterials.length === 0 ? (
                <EmptyState title="No materials found" message="Try another category, brand, or price range." />
              ) : (
                filteredMaterials.map(mat => <MaterialProductCard key={mat._id} mat={mat} />)
              )}
            </div>
          </div>
        )}
      </div>

      {showLocationPicker && (
        <LocationPickerModal
          title="Your Location"
          onClose={() => setShowLocationPicker(false)}
          onSelect={(loc) => { saveLocation(loc); setShowLocationPicker(false); setSortBy('nearest'); }}
        />
      )}
    </div>
  );
}

function ShopCard({ shop, userLocation }) {
  const distance = shop.distance;
  return (
    <Link to={`/shops/${shop._id}`} className="shop-result-card">
      <div className="shop-result-accent" />
      <div className="shop-result-top">
        <div className="shop-result-avatar">
          <Store size={22} />
        </div>
        <div className="shop-result-title">
          <h2>{shop.shopName}</h2>
          <div>
            <MapPin size={11} /> {shop.location?.city}, {shop.location?.state}
          </div>
        </div>
        <span className={`shop-result-status ${shop.isOpen !== false ? 'open' : 'closed'}`}>
          {shop.isOpen !== false ? 'Open' : 'Closed'}
        </span>
      </div>

      <div className="shop-result-metrics">
        <span className="rating"><Star size={13} fill="currentColor" /><strong>{shop.rating?.toFixed(1) || '4.5'}</strong> ({shop.totalRatings || 0})</span>
        <span className="verified"><ShieldCheck size={13} /> Verified</span>
        {distance !== null && (
          <span><Navigation size={13} />
            {distance < 1 ? `${Math.round(distance * 1000)}m away` : `${distance.toFixed(1)}km away`}
          </span>
        )}
      </div>

      {shop.location?.address && (
        <div className="shop-result-address">
          <MapPin size={13} /> <span>{shop.location.address}</span>
        </div>
      )}

      {shop.description && <p className="shop-result-description">{shop.description}</p>}

      {shop.offers?.length > 0 && (
        <div className="shop-result-offer">
          <Tag size={13} /> <span>{shop.offers[0].title}</span>
        </div>
      )}

      <div className="shop-result-footer">
        <span>View shop and materials</span><ArrowRight size={15} />
      </div>
    </Link>
  );
}

function MaterialFilters({ category, setCategory, brandFilter, setBrandFilter, materialBrands, activeMaxPrice, maxMaterialPrice, setMaxPriceFilter, categoryOptions }) {
  const filterCategories = categoryOptions?.length ? categoryOptions : DEFAULT_CATEGORIES;
  return (
    <aside className="materials-filter-panel">
      <h3>Filters</h3>

      <div className="materials-filter-group">
        <div className="materials-filter-title">Category</div>
        <div className="materials-radio-list">
          {filterCategories.map(cat => (
            <label key={cat.key || 'all'} className="materials-radio-row">
              <input
                type="radio"
                name="material-category"
                checked={category === cat.key}
                onChange={() => setCategory(cat.key)}
              />
              <span>{cat.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="materials-filter-group">
        <div className="materials-filter-title">Brand</div>
        <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)} className="materials-filter-select">
          <option value="">All Brands</option>
          {materialBrands.map(brand => <option key={brand} value={brand}>{brand}</option>)}
        </select>
      </div>

      <div className="materials-filter-group">
        <div className="materials-filter-title">Price range</div>
        <input
          type="range"
          min="0"
          max={maxMaterialPrice || 0}
          value={activeMaxPrice || 0}
          onChange={e => setMaxPriceFilter(Number(e.target.value))}
          className="materials-price-range"
        />
        <div className="materials-price-row">
          <span>Rs 0</span>
          <strong>Rs {Number(activeMaxPrice || 0).toLocaleString('en-IN')}</strong>
        </div>
      </div>
    </aside>
  );
}

function MaterialCard({ mat }) {
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const catColors = { sand: '#F59E0B', cement: '#6B7280', iron: '#374151', bricks: '#DC2626', long_bricks: '#EA580C', short_bricks: '#92400E' };
  const color = catColors[mat.category] || 'var(--orange)';
  const wishlisted = isWishlisted(mat._id);
  const owner = { _id: mat.owner?._id, shopName: mat.owner?.shopName, location: mat.owner?.location };

  const handleWishlist = (e) => {
    e.preventDefault();
    toggleWishlist(mat, owner);
    toast.info(wishlisted ? 'Removed from wishlist' : 'Added to wishlist', { autoClose: 1000 });
  };

  const handleCart = (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'customer') { toast.warning('Only customers can add to cart'); return; }
    if (!mat.owner?._id) { toast.error('This material is not linked to an active shop'); return; }
    addToCart(mat, owner, 1, null);
    toast.success(`${mat.name} added to cart!`, { autoClose: 1200 });
  };

  return (
    <div style={{ background: 'white', borderRadius: 13, boxShadow: 'var(--shadow)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Link to={`/shops/${mat.owner?._id}`} style={{ textDecoration: 'none', color: 'var(--dark)', padding: 16, display: 'block', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
          <div style={{ background: color + '15', color, padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>{mat.category?.replace('_', ' ')}</div>
          {mat.offer?.discount && <div style={{ background: '#ECFDF5', color: 'var(--green)', padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{mat.offer.discount}% OFF</div>}
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-main)', marginBottom: 3 }}>{mat.name}</div>
        {mat.brand && <div style={{ color: 'var(--gray)', fontSize: 12, marginBottom: 7 }}>{mat.brand}</div>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--orange)' }}>
            ₹{mat.price?.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray)' }}>/ {mat.unit}</span>
          </div>
          <div style={{ fontSize: 11, color: mat.stock > 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{mat.stock > 0 ? `${mat.stock} avail.` : 'Out of stock'}</div>
        </div>
        {mat.owner?.location?.city && (
          <div style={{ color: 'var(--gray)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
            <MapPin size={10} /> {mat.owner.location.city} · {mat.owner.shopName}
          </div>
        )}
      </Link>

      {/* Action buttons */}
      <div style={{ display: 'flex', borderTop: '1px solid var(--gray-border)' }}>
        <button onClick={handleWishlist}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px', background: wishlisted ? '#FEF2F2' : 'white', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: wishlisted ? 'var(--red)' : 'var(--gray)', borderRight: '1px solid var(--gray-border)' }}>
          <Heart size={13} fill={wishlisted ? 'var(--red)' : 'none'} color={wishlisted ? 'var(--red)' : 'var(--gray)'} />
          {wishlisted ? 'Saved' : 'Wishlist'}
        </button>
        <button onClick={handleCart} disabled={mat.stock === 0}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px', background: mat.stock === 0 ? 'var(--gray-light)' : 'var(--orange-soft)', border: 'none', cursor: mat.stock === 0 ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600, color: mat.stock === 0 ? 'var(--gray)' : 'var(--orange)' }}>
          <ShoppingCart size={13} />
          {mat.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
}

function MaterialProductCard({ mat }) {
  const { toggle: toggleWishlist, isWishlisted } = useWishlist();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showQuote, setShowQuote] = useState(false);
  const wishlisted = isWishlisted(mat._id);
  const owner = { _id: mat.owner?._id, shopName: mat.owner?.shopName, location: mat.owner?.location };
  const categoryLabel = MATERIAL_CATEGORY_LABELS[mat.category] || mat.category || 'Material';
  const productImage = mat.images?.[0];
  const minOrder = mat.minOrder || mat.minimumOrder || 1;
  const distanceLabel = mat.distance !== null && mat.distance !== undefined
    ? (mat.distance < 1 ? `${Math.round(mat.distance * 1000)} m away` : `${mat.distance.toFixed(1)} km away`)
    : 'Distance unavailable';
  const deliveryLabel = mat.owner?.deliveryTime || mat.deliveryTime || 'Delivery time unavailable';
  const sellerName = mat.owner?.shopName || mat.brand || 'Material Stock Supplier';

  const handleWishlist = (e) => {
    e.preventDefault();
    toggleWishlist(mat, owner);
    toast.info(wishlisted ? 'Removed from wishlist' : 'Added to wishlist', { autoClose: 1000 });
  };

  const handleCart = (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'customer') { toast.warning('Only customers can add to cart'); return; }
    if (!mat.owner?._id) { toast.error('This material is not linked to an active shop'); return; }
    addToCart(mat, owner, 1, null);
    toast.success(`${mat.name} added to cart!`, { autoClose: 1200 });
  };

  const handleQuote = (e) => {
    e.preventDefault();
    setShowQuote(true);
  };

  return (
    <>
    <article className="material-product-card">
      <Link to={`/materials/${mat._id}`} className="material-product-link">
        <div className="material-product-media">
          <span className="material-product-chip">{mat.brand || categoryLabel}</span>
          <button onClick={handleWishlist} className={`material-wishlist ${wishlisted ? 'active' : ''}`} title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}>
            <Heart size={15} fill={wishlisted ? 'currentColor' : 'none'} />
          </button>
          {productImage ? (
            <img src={productImage} alt={mat.name} />
          ) : (
            <div className="material-image-placeholder">
              <Package size={28} />
              <span>Product image</span>
            </div>
          )}
        </div>

        <div className="material-product-body">
          <div className="material-product-main-row">
            <div className="material-product-title-wrap">
              <h3>{mat.name}</h3>
              <div className="material-product-unit">{mat.unit || 'piece'}</div>
            </div>
            <div className="material-product-price">Rs {Number(mat.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div className="material-seller-name">{sellerName}</div>

          <div className="material-meta-pills">
            <span><MapPin size={13} /> {distanceLabel}</span>
            <span><Clock size={13} /> {deliveryLabel}</span>
          </div>

          <div className="material-delivery-line">
            Delivery: {deliveryLabel} / Seller delivery
          </div>
          <div className="material-min-order">
            Min. order: {Number(minOrder).toLocaleString('en-IN')} {mat.unit || 'units'}
          </div>
        </div>
      </Link>

      <div className="material-card-actions">
        <button onClick={handleCart} disabled={mat.stock === 0} className="material-add-btn">
          <ShoppingCart size={17} /> {mat.stock === 0 ? 'Out of Stock' : 'Add'}
        </button>
        <button onClick={handleQuote} className="material-quote-btn">
          <FileText size={17} /> Quote
        </button>
      </div>
    </article>
    {showQuote && <QuoteRequestModal material={mat} onClose={() => setShowQuote(false)} />}
    </>
  );
}
